/* =========================================================================
   First-game warm-up — a brand-new player's Round 1 serves only gentle
   questions so they build confidence before the real curve starts. "Gentle"
   mirrors the DB seed's level heuristic (db/seed generator levelFor(): the
   History / Doctrine / Popes categories are Level 1, everything else Level 0),
   so client and server agree on what counts as easy.

   "New player" is device-local: until one full game has been completed on
   this device (localStorage counter), Round 1 is eased. Pure logic here;
   gameController wires it. Mirrors the dyk.ts / registerPrompt.ts pattern.
   ========================================================================= */
import type { Question } from './engine';

const GAMES_KEY = 'catholics.gamesCompleted';

/** Categories the seed generator rates Level 1 (harder) — excluded from warm-up. */
export const WARMUP_HARD_CATEGORIES: ReadonlySet<string> = new Set(['History', 'Doctrine', 'Popes']);

/** Per-question exclusions: friendly-category items that are still too heavy or
    scholarly for a game's opening round (Eric's rule: Round 1 = easy, basic Bible
    territory). 35 = Kolbe/Auschwitz · 9 = the Summa · 31 = Confessions/City of God ·
    63 = The Interior Castle. */
export const WARMUP_EXCLUDED_IDS: ReadonlySet<number> = new Set([9, 31, 35, 63]);

export function isWarmupFriendly(category: string): boolean {
  return !WARMUP_HARD_CATEGORIES.has(category);
}

/** Round 1 easing — TRUE BEGINNERS ONLY (PLAN-progression §6 Phase 1.3).
    It applies when all three hold:
      • it's Round 1 (hard mode can't reach Round 1 — the picker starts between
        rounds — so this never fights an explicit difficulty choice), AND
      • the player's career level is ≤ 1 (0 = unknown/offline → treated as new), AND
      • no game has been completed on this device yet.
    A Level-8 veteran therefore opens on the real ramp instead of the beginner
    warm-up that used to run in EVERY game. Defaults keep the old first-game
    behaviour for callers that know neither signal. PURE. */
export function shouldEaseRound(roundIndex: number, careerLevel = 0, gamesCompleted = 0): boolean {
  return roundIndex === 0 && careerLevel <= 1 && gamesCompleted <= 0;
}

/** Kept for API stability where first-game specificity is still wanted. */
export function shouldEaseFirstRound(gamesCompleted: number, roundIndex: number): boolean {
  return roundIndex === 0 && gamesCompleted <= 0;
}

/** The eased pool: friendly questions minus the per-question exclusions, topped up
    from the full pack only if the pool can't fill the round (crash-proof; order is
    later shuffled by RoundEngine; a top-up only happens when easing is impossible). */
export function warmupPool(questions: readonly Question[], count: number): Question[] {
  const friendly = questions.filter(
    (q) => isWarmupFriendly(q.category) && !WARMUP_EXCLUDED_IDS.has(q.id),
  );
  if (friendly.length >= count) return friendly;
  return friendly.concat(questions.filter((q) => !friendly.includes(q)));
}

/* ---------- round difficulty progression (Eric, 2026-07-24 playtest) ----------
   "Easy questions in Level 1 always. Level 2 can go a little harder. We want
   progression as the rounds go up." Questions carry an optional hardness tag
   (engine.ts: absent = 1 easy/popular · 2 = a little harder · 3 = heavy); each
   round caps the hardness it will serve. Round 1 additionally keeps the gentle
   category warm-up above. 🔥 Hard mode bypasses all of this — an explicit choice. */

/** Max servable hardness per round index (R1..R5). */
export const MAX_HARDNESS_BY_ROUND: readonly number[] = [1, 2, 2, 3, 3];

/** The round's difficulty-capped pool, topped up from the full pack only if the
    cap can't fill the round (same crash-proof pattern as warmupPool). */
export function roundPool(questions: readonly Question[], roundIndex: number, count: number): Question[] {
  const cap = MAX_HARDNESS_BY_ROUND[roundIndex] ?? 3;
  const eligible = questions.filter((q) => (q.hardness ?? 1) <= cap);
  if (eligible.length >= count) return eligible;
  return eligible.concat(questions.filter((q) => !eligible.includes(q)));
}

/* ---------- device-local games-completed counter ---------- */
export function getGamesCompleted(): number {
  try {
    const n = parseInt(localStorage.getItem(GAMES_KEY) || '0', 10);
    return Number.isFinite(n) && n > 0 ? n : 0;
  } catch {
    return 0; // storage unavailable → treat as new player (safe default)
  }
}

export function recordGameCompleted(): void {
  try {
    localStorage.setItem(GAMES_KEY, String(getGamesCompleted() + 1));
  } catch {
    /* storage unavailable — warm-up will simply repeat, which is harmless */
  }
}
